import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { RewardProgressProps } from 'shared/interfaces/reward-progress/reward-progress.interface';
import Coin from 'src/assets/images/icons/custom-icons/Coin';
import LinearProgressBar from 'src/components/ProgressBar/ProgressBar';
import { ProgressBarColorCombination } from 'src/components/ProgressBar/progress-bar-color-combination.enum';
import { useProgressAnimation } from 'src/hooks/use-progress-animation';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';

const RewardBadge = ({
  rewardImage,
  rewardValueLabel,
  showRewardImage,
  isRewardReady,
  defaultAlt,
  goalLabel,
}: {
  rewardImage?: string | null;
  rewardValueLabel?: string | null;
  showRewardImage?: boolean;
  isRewardReady: boolean;
  defaultAlt: string;
  goalLabel: string;
}) => {
  return (
    <div className="relative group">
      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-purple-400 to-violet-500 blur-sm opacity-20 transition-opacity group-hover:opacity-30" />
      <div
        className={classNames(
          'relative flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border-2 transition-all duration-300',
          {
            'border-emerald-300 bg-gradient-to-br from-emerald-400 to-teal-500 shadow-lg shadow-emerald-500/20':
              isRewardReady,
            'border-purple-300 bg-gradient-to-br from-purple-400 to-violet-500 shadow-md': !isRewardReady,
          }
        )}
      >
        {rewardImage && showRewardImage ? (
          <img src={rewardImage} alt={defaultAlt} className="h-full w-full object-cover" />
        ) : (
          <div className="flex flex-col items-center justify-center gap-0.5 px-1">
            <Coin className="h-3.5 w-3.5 text-white" />
            {rewardValueLabel ? (
              <span className="max-w-[2.3rem] truncate text-[10px] font-bold leading-none text-white font-zillaSlab">
                {rewardValueLabel}
              </span>
            ) : (
              <span className="text-[9px] font-bold leading-none tracking-[0.14em] text-white/85">{goalLabel}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const RewardProgress: React.FC<RewardProgressProps> = ({ ...props }) => {
  const {
    points,
    rewardImage,
    reward,
    pointsEarned,
    targetPointsForReward,
    previousPoints = 0,
    shouldAnimate = true,
    rewardAmount,
    rewardCurrencySymbol,
    showRewardImage = false,
  } = props;

  const t = useTranslationByKey('COMPONENTS.REWARD_PROGRESS');
  const { formatPoints } = useUserCurrency();
  const [showReadyText, setShowReadyText] = useState(false);

  const { animatedValue, isRewardReady } = useProgressAnimation({
    startValue: previousPoints,
    endValue: points,
    targetValue: targetPointsForReward,
    shouldAnimate,
  });

  // After animation completes and reward is ready, show the text "Ready to redeem"
  useEffect(() => {
    if (isRewardReady && animatedValue === points && shouldAnimate) {
      const timer = setTimeout(() => {
        setShowReadyText(true);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      setShowReadyText(isRewardReady);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRewardReady, animatedValue, points]);

  const pointsRemaining = Math.max(targetPointsForReward - animatedValue, 0);
  const progressPercentage =
    targetPointsForReward > 0 ? Math.min((animatedValue / targetPointsForReward) * 100, 100) : 0;
  const rewardValueLabel =
    rewardAmount !== undefined && rewardAmount !== null
      ? `${rewardCurrencySymbol ?? ''}${rewardAmount.toLocaleString()}`
      : null;
  const readyLabel = `${t('READY_TO_REDEEM_PART_1')} ${t('READY_TO_REDEEM_PART_2')}`;
  const progressBadgeLeft = progressPercentage === 0 ? '8%' : `${Math.min(Math.max(progressPercentage, 8), 95)}%`;

  return (
    <div className="relative overflow-hidden rounded-lg border border-purple-100 bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 shadow-sm">
      {isRewardReady && showReadyText && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-emerald-400/15 via-teal-400/10 to-transparent" />
      )}

      <div className="relative z-10 p-3">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800 font-spaceGrotesk">{t('REWARD_PROGRESS')}</h3>

          {!!pointsEarned && (
            <div className="flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-400 to-violet-500 px-2 py-1 shadow-sm">
              <Coin className="h-3 w-3 text-white" />
              <span className="text-xs font-bold text-white">+{formatPoints(pointsEarned)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <RewardBadge
              rewardImage={rewardImage}
              rewardValueLabel={rewardValueLabel}
              showRewardImage={showRewardImage}
              isRewardReady={isRewardReady && showReadyText}
              defaultAlt={t('DEFAULT_ALT')}
              goalLabel={t('GOAL')}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    {rewardValueLabel && (
                      <span className="flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-base font-bold text-transparent font-zillaSlab">
                        {rewardValueLabel}
                      </span>
                    )}
                    {reward && (
                      <span
                        className={classNames('truncate text-sm font-medium font-spaceGrotesk', {
                          'text-emerald-600': isRewardReady && showReadyText,
                          'text-gray-700': !(isRewardReady && showReadyText),
                        })}
                      >
                        {reward}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  {isRewardReady && showReadyText ? (
                    <p className="text-xs font-bold text-emerald-600 font-spaceGrotesk">{readyLabel}</p>
                  ) : (
                    <>
                      <div className="text-xs font-bold text-gray-600">{formatPoints(pointsRemaining)}</div>
                      <p className="text-xs text-gray-500 font-spaceGrotesk">{t('TO_REWARD')}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="relative">
                <LinearProgressBar
                  value={animatedValue}
                  range={targetPointsForReward}
                  colorCombination={ProgressBarColorCombination.GreenForegroundWithGreyBackground}
                />

                <div
                  className="absolute top-1/2 -translate-y-1/2 transition-all duration-300 ease-out"
                  style={{
                    left: progressBadgeLeft,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <span className="whitespace-nowrap rounded-full bg-purple-600 px-1.5 py-0.5 text-xs font-bold text-white shadow-sm">
                    {Math.round(progressPercentage)}%
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-p3 font-medium text-gray-700">
              <span className="flex items-center gap-0.5">
                <Coin className="h-4 w-4" />
                <span>{formatPoints(animatedValue)}</span>
              </span>
              <span className="text-gray-500">{t('COLLECTED')}</span>
              <span className="text-gray-400">/</span>
              <span>{formatPoints(targetPointsForReward)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
